/**
 * useFirstDocs — 给一组项目，返回 { [name]: { firstDoc, querying, probed } }
 *
 * 四层探测流水线（按代价排序）：
 *   1. 内联 — 项目对象自带 timeline → extractFirstDoc（0 网络成本）
 *   2. localStorage — 全局 24h 缓存（0 网络成本）
 *   3. /api/firstdoc-bulk — 服务端 SQL 反查 project_cache（1 次 HTTP）
 *   4. /api/firstdoc-batch — Wayback 兜底探测（autoProbe=true 时，按 10 个一批）
 *
 * 整个流水线幂等 + 自缓存：
 *   - 每层结果立刻 setMap（渐进式 UI 更新）
 *   - 每层结果写回 localStorage（下次秒命中）
 *   - cancelled 闸门防止 unmount 后 setState
 *
 * @param {Array<{name, id?, timeline?, details?}>} items
 * @param {{ autoProbe?: boolean }} options
 * @returns {{ [name]: { firstDoc: object|null, querying: bool, probed: bool } }}
 */
import { useState, useEffect } from 'react';
import { extractFirstDoc } from './react/FirstDocCell';
import { loadCache, saveCacheBatch } from './firstDocCache';

const BATCH_SIZE = 10;
const BATCH_GAP_MS = 500;

export function useFirstDocs(items, { autoProbe = true } = {}) {
  const [map, setMap] = useState({});

  // 用名字列表的字符串化做稳定依赖键，防止数组引用变化触发无谓重跑
  const namesKey = (items || []).map(p => p?.name || '').filter(Boolean).join('|');

  useEffect(() => {
    if (!items || items.length === 0) return;
    let cancelled = false;

    const run = async () => {
      // Phase 1 — 内联 timeline
      const next = {};
      items.forEach(p => {
        if (!p?.name) return;
        const fd = extractFirstDoc(p);
        if (fd) next[p.name] = { firstDoc: fd, querying: false, probed: true };
      });

      // Phase 2 — localStorage（仅保留对当前项目相关的条目）
      const ls = loadCache();
      const nameSet = new Set((items || []).map(p => p?.name).filter(Boolean));
      Object.entries(ls).forEach(([n, e]) => {
        if (!next[n] && nameSet.has(n)) {
          next[n] = { firstDoc: e.firstDoc, querying: false, probed: true };
        }
      });
      if (!cancelled) setMap({ ...next });

      // Phase 3 — /api/firstdoc-bulk SQL 反查
      const missingNames = items
        .map(p => p?.name)
        .filter(n => n && !next[n]);

      if (missingNames.length > 0) {
        try {
          const res = await fetch('/api/firstdoc-bulk', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ names: missingNames.slice(0, 100) }),
          });
          const json = await res.json();
          if (!cancelled && json?.ok && json.results) {
            const lsBatch = {};
            Object.entries(json.results).forEach(([name, fd]) => {
              next[name] = { firstDoc: fd, querying: false, probed: true };
              lsBatch[name] = fd;
            });
            if (Object.keys(lsBatch).length > 0) saveCacheBatch(lsBatch);
            setMap({ ...next });
          }
        } catch { /* 静默失败 */ }
      }

      // Phase 4 — Wayback 兜底（autoProbe）
      if (!autoProbe || cancelled) return;
      const stillMissing = items.filter(p => p?.name && !next[p.name]);
      if (stillMissing.length === 0) return;

      // 标记"探测中"，让 FirstDocCell 显示 ···
      stillMissing.forEach(p => {
        next[p.name] = { firstDoc: null, querying: true, probed: false };
      });
      setMap({ ...next });

      for (let i = 0; i < stillMissing.length; i += BATCH_SIZE) {
        if (cancelled) return;
        const batch = stillMissing.slice(i, i + BATCH_SIZE);
        try {
          const res = await fetch('/api/firstdoc-batch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              projects: batch.map(p => ({
                project_name: p.name,
                project_id: p.id || p.details?.project_id,
              })),
            }),
          });
          const json = await res.json();
          if (cancelled) return;
          if (json?.ok && json.results) {
            const lsBatch = {};
            Object.entries(json.results).forEach(([name, fd]) => {
              // 新 error 形状：{ error: 'reason' } → 不写缓存，标记 probed=false
              //                让下次页面打开重新触发探测
              if (fd && typeof fd === 'object' && fd.error) {
                next[name] = { firstDoc: null, querying: false, probed: false };
                return;
              }
              next[name] = { firstDoc: fd, querying: false, probed: true };
              lsBatch[name] = fd;
            });
            if (Object.keys(lsBatch).length > 0) saveCacheBatch(lsBatch);
            setMap({ ...next });
          } else {
            // ok=false → 不缓存，允许下次重试
            batch.forEach(p => {
              next[p.name] = { firstDoc: null, querying: false, probed: false };
            });
            setMap({ ...next });
          }
        } catch {
          // 网络失败 → 不缓存，允许下次重试
          batch.forEach(p => {
            next[p.name] = { firstDoc: null, querying: false, probed: false };
          });
          if (!cancelled) setMap({ ...next });
        }
        if (i + BATCH_SIZE < stillMissing.length) {
          await new Promise(r => setTimeout(r, BATCH_GAP_MS));
        }
      }
    };

    run();
    return () => { cancelled = true; };
  }, [namesKey, autoProbe]);

  return map;
}
